/*
 * ***********************************************************************
 * Bounteous CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Bounteous.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property
 * of Bounteous and its suppliers, if any. The intellectual and
 * technical concepts contained herein are proprietary to Bounteous
 * and its suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Bounteous.
 * ***********************************************************************
 */

/**
 * Affinity Teaser.
 */

import { Component, registerComponent } from 'js/component.js';
import { getViewportSize, isEditMode } from 'js/helpers.js';

const componentName = 'cmp-affinity-teaser';

class AffinityTeaser extends Component {

    elements = {
        title: this.element.querySelector(`.${componentName}__title`),
        eyebrow: this.element.querySelector(`.${componentName}__eyebrow`),
        defaultLink: this.element.querySelector(`.${componentName}__default-link`),
        industryLink: this.element.querySelector(`.${componentName}__industry-link`)
    };

    select = this.element.querySelector('.cta-dropdown select');

    currentViewport = getViewportSize();

    /**
     *
     * Updates copy for the dynamic affinity elements.
     */
    updateAffinityElements(name) {
        const selectOption = this.element.querySelector(`[text="${name}"]`);

        if(selectOption) {
            const { linkLabel } = selectOption.dataset;
            const { href } = selectOption.dataset;

            if (!linkLabel || !href) {
                this.elements.industryLink.classList.add(`${componentName}__industry-link--hidden`);
            } else {
                this.elements.industryLink.classList.remove(`${componentName}__industry-link--hidden`);
                this.elements.industryLink.innerHTML = linkLabel;
                this.elements.industryLink.setAttribute('href', href);
            }

            this.elements.title.innerHTML = `${name} <span class="${componentName}__category">${this.getAffinityCategory()}</span>`;

            const { eyebrowText } = this.elements.eyebrow.dataset;
            this.elements.eyebrow.innerHTML = eyebrowText;
        } else {
            this.elements.industryLink.classList.add(`${componentName}__industry-link--hidden`);
        }
    }

    /**
     *
     * Checks if an affinity exists for a user.
     */
    isKnownUser() {
        const affinities = JSON.parse(window.localStorage.getItem('affinity'));
        return affinities && affinities[this.getAffinityCategory()];
    }

    /**
     * Populates Affinity if it exists in localStorage
     */
    populateKnownUserAffinity() {
        if (this.isKnownUser()) {
            const affinities = JSON.parse(window.localStorage.getItem('affinity'));
            const { name } = affinities[this.getAffinityCategory()];
            this.updateAffinityElements(name);
        }
    }

    /**
     * Toggles the active class for the component
     */
    toggleActiveClass(isKnownUser) {
        if (isKnownUser) {
            this.element.classList.add(`${componentName}--known-user`);
        } else {
            this.element.classList.remove(`${componentName}--known-user`);
        }
    }

    /**
     * Scrolls to the carousel on mobile.
     */
    scrollToContent() {
        const isMobile = this.currentViewport === 'mobile';
        if (isMobile) {
            const affinityContent = this.element.parentNode.parentNode.nextElementSibling;
            affinityContent.scrollIntoView();
        }
    }

    /**
     * Get affinity category
     */

    getAffinityCategory() {
        let category = null;

        if (this.select) {
            category = this.select.dataset.tagNamespace.split('/')[1]; // eslint-disable-line prefer-destructuring
        }

        return category;
    }

    /**
     * Add event listeners
     */
    addEventListeners () {
        window.addEventListener('SDI-DLM:affinity', (event) => {
            const { category, name } = event.detail;
            if (this.select.dataset.tagNamespace.indexOf(category) !== -1) {
                this.updateAffinityElements(name);
                this.toggleActiveClass(true);
                this.scrollToContent();
            }
        });
    }

    /**
     * Initialize Component
     */
    init() {
        if (!isEditMode()) {
            this.addEventListeners();
            this.populateKnownUserAffinity();
            this.toggleActiveClass(this.isKnownUser());
        }
    }
}

registerComponent(`.${componentName}`, AffinityTeaser);
